import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import html2canvas from 'html2canvas';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useTheme } from '../contexts/ThemeContext';
import MapToolbar from './map/MapToolbar';
import MeasurementPanel from './map/MeasurementPanel';
import ShapePropertiesPanel from './map/ShapePropertiesPanel';
import PropertyInfoPanel from './map/PropertyInfoPanel';
import {
  GOOGLE_KEY, LIBRARIES, MAP_ID, MAP_TYPES, mkDrawOpts,
  buildMeasurement, getShapeType, createMarker, isMarker,
  createSiteMarker, SITE_LOGISTICS
} from './map/mapUtils';
import { BOUNDARY_COLOURS, pickProviderForLatLng } from './map/BoundariesControl';
import { CustomDrawingController } from './map/CustomDrawingController';
import { getStableFeatureId, normalizeParcelDetails } from './map/parcelUtils';
import { buildCanvasTileMapType } from './map/cadastreTileUtils';
import { formatShapeLabel, buildTooltipHTML, getShapePosition } from './map/shapeFormatters';
import { useBoundariesOverlay } from './map/useBoundariesOverlay';
import { useParcelInspect } from './map/useParcelInspect';
import { ImageryDatePill } from './map/ImageryDatePill';
import { ViewModePill } from './map/ViewModePill';
import { StreetViewPanel } from './map/StreetViewPanel';
import { useAuth } from '../contexts/AuthContext';
import { getCapabilities } from '../lib/planFeatures';

// Wayback zoom cap — Esri only serves PNG tiles up to zoom 20 for most
// regions; zoom 21+ returns CORB-blocked 404s (blank grey map). Used both
// by the ImageMapType definition and by applyMapType to clamp the map's
// own maxZoom while a historical date is active.
const WAYBACK_MAX_ZOOM = 20;

// NOTE on `try { ... } catch (e) {}` blocks throughout this file:
// They wrap Google Maps SDK calls during shape/marker/listener teardown (setMap(null),
// removeAt, clearInstanceListeners, etc). The SDK can throw if the underlying overlay
// is already disposed or the map instance is gone (e.g. unmount race). Swallowing is
// intentional — we don't want unmount errors to crash the React tree or pollute logs
// in production. Do NOT add logging to these without auditing each call site.

export default function MapView({ project, onSaveMeasurement, onDeleteMeasurement, onUpdateMeasurement, onExportPDF, onSaveLocation, onMapInstance, readOnly = false, projectApiPath }) {
  // Quick Look and Project both store boundary_preferences + imagery_date the
  // same way under the hood, but on different endpoints. `projectApiPath`
  // lets the parent override the PUT target — e.g. Quick Look passes
  // "/quick-look/by-place?place_id=…". Defaults to /projects/:id so all
  // existing call-sites keep working unchanged.
  const apiPathBase = projectApiPath || (project?.id ? `/projects/${project.id}` : null);
  const { user, apiCall } = useAuth();
  const capabilities = useMemo(() => getCapabilities(user), [user]);
  const mapRef = useRef(null);
  const shapesRef = useRef([]);
  const measurementsRef = useRef([]);
  const recalcTimerRef = useRef(null);
  const customClickRef = useRef(null);
  const tempMarkersRef = useRef([]);
  const tempLineRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const labelMarkersRef = useRef([]);
  const saveLocationTimer = useRef(null);

  const [mapType, setMapType] = useState('satellite');
  // View mode — '2d' (top-down), '45' (bird's-eye tilt) or '360' (Street
  // View modal). Lives separately from mapType so users can flip between
  // satellite/roadmap without losing the tilt preference. Defaults to '45'
  // to match the historical behaviour of opening every project tilted.
  const [viewMode, setViewMode] = useState('45');
  const [streetViewOpen, setStreetViewOpen] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [drawingMode, setDrawingMode] = useState(null);
  const [customTool, setCustomTool] = useState(null);
  const [customPoints, setCustomPoints] = useState([]);
  const customPointsRef = useRef([]);
  useEffect(() => { customPointsRef.current = customPoints; }, [customPoints]);
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [addressMarker, setAddressMarker] = useState(null);
  const [addressText, setAddressText] = useState(project?.map_data?.address || '');
  const [selectedColor] = useState('#7367F0');
  const [selectedOpacity] = useState(0.12);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const selectedShapeIdRef = useRef(null);
  const { theme } = useTheme();

  const center = useMemo(() => {
    const loc = project?.location || { lat: 51.5074, lng: -0.1278 };
    return { lat: loc.lat, lng: loc.lng };
  }, [project]);
  const zoom = project?.location?.zoom || 20;

  // Stable options object — passing a fresh literal each render would trigger
  // map.setOptions() on every state change, which in vector-mode (mapId set)
  // invalidates the overlay tile cache and causes the tint layer to flicker.
  const mapOptions = useMemo(() => ({
    mapId: MAP_ID,
    mapTypeControl: false,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    tilt: 45,
    rotateControl: true,
    scaleControl: true,
    clickableIcons: false,
  }), []);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_KEY, libraries: LIBRARIES });
  const [mapReady, setMapReady] = useState(false);

  // Boundaries (cadastre) overlay — state is stored PER-PROJECT on the project
  // document, so each project remembers its own toggle/colour/opacity.
  // Defaults for a brand-new project: OFF, 0% wash, Yellow stroke (index 2).
  // We start with the same defaults locally and hydrate from `project.boundary_preferences`
  // once the project doc arrives.
  const DEFAULT_BOUNDARY_PREFS = { enabled: false, opacity: 0, colourIndex: 2 };
  const [boundariesEnabled, setBoundariesEnabled] = useState(DEFAULT_BOUNDARY_PREFS.enabled);
  const [boundariesOpacity, setBoundariesOpacity] = useState(DEFAULT_BOUNDARY_PREFS.opacity);
  const [boundariesColourIndex, setBoundariesColourIndex] = useState(DEFAULT_BOUNDARY_PREFS.colourIndex);
  // Tracks whether we've already applied the saved server-side prefs once for
  // THIS project. Until this is true, we don't fire any persistence PATCH —
  // otherwise the initial defaults would clobber the saved values.
  const [boundaryPrefsHydrated, setBoundaryPrefsHydrated] = useState(false);
  // Cadastre line opacity is fixed at 80% — blends nicely with satellite without overpowering it.
  const BOUNDARY_LINE_OPACITY = 0.8;
  const boundariesOverlayRef = useRef(null);
  const boundariesStyleRef = useRef(null);
  // Vector parcel layer (per-property hover/click) — only active at zoom ≥ 17.
  const parcelDataLayerRef = useRef(null);
  const parcelFetchSeqRef = useRef(0);
  // Track selection by stable feature ID (OBJECTID from the WFS payload) rather
  // than by object reference, because `idle`-triggered re-fetches replace the
  // feature objects. The ID survives across refetches so we can re-apply the
  // highlight to whichever feature object currently represents the same parcel.
  const selectedParcelIdRef = useRef(null);
  const [propertyInfo, setPropertyInfo] = useState(null);

  // Latest-callback registry consumed by useParcelInspect — the click handler
  // inside the data-layer listener calls these via .current.* so it always
  // sees the current versions of findUserShapeAt / selectShapeById /
  // deselectAllShapes (which are defined later in this component) without
  // needing the click listener to be re-bound on every render.
  const parcelCallbacksRef = useRef({
    findUserShapeAt: () => null,
    selectShapeById: () => {},
    deselectAllShapes: () => {},
  });

  // One-time hydration from the project's saved boundary_preferences.
  // Runs as soon as the project document arrives (or whenever the user opens
  // a different project — keyed on `project?.id`).
  useEffect(() => {
    if (!project?.id) return;
    const bp = project.boundary_preferences || {};
    setBoundariesEnabled(typeof bp.enabled === 'boolean' ? bp.enabled : DEFAULT_BOUNDARY_PREFS.enabled);
    setBoundariesOpacity(typeof bp.opacity === 'number' ? bp.opacity : DEFAULT_BOUNDARY_PREFS.opacity);
    setBoundariesColourIndex(typeof bp.colour_index === 'number' ? bp.colour_index : DEFAULT_BOUNDARY_PREFS.colourIndex);
    setBoundaryPrefsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Debounced PUT /api/projects/:id with the latest boundary_preferences.
  // Captured payload + cleanup-flush ensure rapid toggle-then-navigate still
  // lands on the server (otherwise the unmount used to drop the pending PATCH).
  const boundaryPrefsSaveTimerRef = useRef(null);
  const pendingBoundaryPatchBodyRef = useRef(null);

  useEffect(() => {
    if (!boundaryPrefsHydrated) return;
    if (!apiPathBase) return;
    const body = {
      boundary_preferences: {
        enabled: boundariesEnabled,
        opacity: boundariesOpacity,
        colour_index: boundariesColourIndex,
      },
    };
    pendingBoundaryPatchBodyRef.current = { url: apiPathBase, body };
    if (boundaryPrefsSaveTimerRef.current) {
      clearTimeout(boundaryPrefsSaveTimerRef.current);
    }
    boundaryPrefsSaveTimerRef.current = setTimeout(() => {
      apiCall('put', apiPathBase, body)
        .then(() => {
          if (pendingBoundaryPatchBodyRef.current?.body === body) {
            pendingBoundaryPatchBodyRef.current = null;
          }
        })
        .catch(() => { /* network/auth blip — next change will retry */ });
    }, 400);
    // NOTE: no cancel-on-cleanup here. Doing so used to drop the pending PUT
    // when the user toggled and navigated within 400ms.
  }, [boundariesEnabled, boundariesOpacity, boundariesColourIndex, apiPathBase, apiCall, boundaryPrefsHydrated]);

  // Unmount-time flush — guarantees the last toggle hits the server even if
  // the user closes / navigates within the debounce window.
  useEffect(() => {
    return () => {
      if (boundaryPrefsSaveTimerRef.current) {
        clearTimeout(boundaryPrefsSaveTimerRef.current);
        boundaryPrefsSaveTimerRef.current = null;
      }
      const pending = pendingBoundaryPatchBodyRef.current;
      if (!pending) return;
      apiCall('put', pending.url, pending.body).catch(() => {});
      pendingBoundaryPatchBodyRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Historical imagery date (Esri Wayback) =====
  // Per-project saved imagery selection. `imageryDate` is either `null` (live
  // Google Maps satellite) or { release_id, date } pointing at a specific
  // Wayback snapshot. Persistence mirrors the boundary_preferences pattern:
  // hydrate from `project.imagery_date` on open, debounced PUT on change.
  const [imageryDate, setImageryDate] = useState(null);
  const [imageryDateHydrated, setImageryDateHydrated] = useState(false);
  const [waybackReleases, setWaybackReleases] = useState([]);
  const [waybackTileTemplate, setWaybackTileTemplate] = useState('');
  const [waybackLoading, setWaybackLoading] = useState(false);
  const imageryDateSaveTimerRef = useRef(null);
  const pendingImageryPatchRef = useRef(null);

  // Hydrate from project on open / project change.
  useEffect(() => {
    if (!project?.id) return;
    const saved = project.imagery_date;
    if (saved && typeof saved.release_id === 'number' && typeof saved.date === 'string') {
      setImageryDate({ release_id: saved.release_id, date: saved.date });
    } else {
      setImageryDate(null);
    }
    setImageryDateHydrated(true);
  }, [project?.id]);

  // Fetch the full Wayback release list once on mount so the pill dropdown is
  // populated. The releases list is ~200 items and cached by the backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWaybackLoading(true);
      try {
        const res = await apiCall('get', '/wayback/releases');
        if (cancelled) return;
        setWaybackReleases(res.data?.releases || []);
        setWaybackTileTemplate(res.data?.tile_url_template || '');
      } catch {
        // Network blip — pill will show empty list; user can still revert to Live.
      } finally {
        if (!cancelled) setWaybackLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall]);

  // Debounced PUT — saves { imagery_date: {...} | null } per project. Same
  // captured-payload + unmount-flush trick as the boundary save effect so a
  // rapid pick-then-navigate still lands on the server.
  useEffect(() => {
    if (!imageryDateHydrated) return;
    if (!apiPathBase) return;
    const body = { imagery_date: imageryDate || null };
    pendingImageryPatchRef.current = { url: apiPathBase, body };
    if (imageryDateSaveTimerRef.current) clearTimeout(imageryDateSaveTimerRef.current);
    imageryDateSaveTimerRef.current = setTimeout(() => {
      apiCall('put', apiPathBase, body)
        .then(() => {
          if (pendingImageryPatchRef.current?.body === body) {
            pendingImageryPatchRef.current = null;
          }
        })
        .catch(() => { /* retry on next change */ });
    }, 400);
  }, [imageryDate, apiPathBase, apiCall, imageryDateHydrated]);

  // Unmount-time flush.
  useEffect(() => {
    return () => {
      if (imageryDateSaveTimerRef.current) {
        clearTimeout(imageryDateSaveTimerRef.current);
        imageryDateSaveTimerRef.current = null;
      }
      const pending = pendingImageryPatchRef.current;
      if (!pending) return;
      apiCall('put', pending.url, pending.body).catch(() => {});
      pendingImageryPatchRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build (and cache) a Google Maps ImageMapType that fetches Wayback tiles
  // for the given release. Returned layer is registered into `map.mapTypes`
  // and activated via setMapTypeId. We keep a per-mount cache so repeated
  // satellite-toggles don't re-instantiate the same overlay.
  //
  // maxZoom is capped at WAYBACK_MAX_ZOOM (defined at module top) because
  // Esri's tile server doesn't serve PNG tiles beyond that — see comment
  // there for the full reasoning.
  const waybackLayerCacheRef = useRef(new Map());
  const buildWaybackLayer = useCallback((release) => {
    if (!release?.release_id || !waybackTileTemplate || !window.google?.maps) return null;
    const cache = waybackLayerCacheRef.current;
    const cached = cache.get(release.release_id);
    if (cached) return cached;
    const layer = new window.google.maps.ImageMapType({
      name: `Wayback ${release.date}`,
      tileSize: new window.google.maps.Size(256, 256),
      maxZoom: WAYBACK_MAX_ZOOM,
      minZoom: 1,
      getTileUrl: (coord, zoom) => waybackTileTemplate
        .replace('{release}', String(release.release_id))
        .replace('{z}', String(zoom))
        .replace('{y}', String(coord.y))
        .replace('{x}', String(coord.x)),
    });
    cache.set(release.release_id, layer);
    return layer;
  }, [waybackTileTemplate]);

  useEffect(() => { measurementsRef.current = measurements; updateShapeLabels(); }, [measurements]);
  useEffect(() => { selectedShapeIdRef.current = selectedShapeId; }, [selectedShapeId]);

  // When the user selects any shape, drop any active parcel highlight + panel.
  // Keeps "selected work" mutually exclusive from "selected parcel" so the
  // properties panel and parcel info panel never overlap in z-space.
  useEffect(() => {
    if (!selectedShapeId) return;
    const layer = parcelDataLayerRef.current;
    const selId = selectedParcelIdRef.current;
    if (layer && selId != null) {
      layer.forEach((f) => {
        if (getStableFeatureId(f) === selId) {
          try { layer.revertStyle(f); } catch { /* layer/feature may already be torn down */ }
        }
      });
    }
    selectedParcelIdRef.current = null;
    setPropertyInfo(null);
  }, [selectedShapeId]);

  // Imperatively manage the address / project-location marker via AdvancedMarkerElement
  // (replaces the deprecated <Marker> JSX from @react-google-maps/api).
  const overlayMarkerRef = useRef(null);
  const didAutoPanRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    // Cleanup prior marker
    if (overlayMarkerRef.current) { try { overlayMarkerRef.current.setMap(null); } catch (e) { /* noop */ } overlayMarkerRef.current = null; }
    let targetPos = null;
    if (addressMarker) {
      targetPos = addressMarker;
      overlayMarkerRef.current = createMarker({
        position: addressMarker,
        map,
        title: addressText || 'Selected location',
        icon: { url: 'pin', color: '#7367F0' },
      });
    } else if (project?.location) {
      // For Quick Look entries we anchor the pin to the originally-picked
      // address lat/lng (saved at seed-time on `map_data.lat / map_data.lng`)
      // rather than `project.location` — because location gets written back
      // on every pan/zoom via the debounced onSaveLocation, so over time it
      // drifts away from the true address. The address-anchored fields are
      // immutable from the user's pick, so the pin stays planted forever.
      const md = project.map_data || {};
      const pinLat = (typeof md.lat === 'number') ? md.lat : project.location.lat;
      const pinLng = (typeof md.lng === 'number') ? md.lng : project.location.lng;
      targetPos = { lat: pinLat, lng: pinLng };
      overlayMarkerRef.current = createMarker({
        position: targetPos,
        map,
        title: project?.map_data?.address || project?.name || 'Project location',
        icon: { url: 'pin', color: '#EA5455' },
      });
    }
    // Auto-pan to the project location on first load (only once) so the pin is always in view.
    // Skip if the user already has measurements rendered (their work matters more).
    if (targetPos && !didAutoPanRef.current && (measurementsRef.current?.length ?? 0) === 0) {
      didAutoPanRef.current = true;
      try { map.panTo(targetPos); } catch (e) { /* noop */ }
    }
    return () => {
      if (overlayMarkerRef.current) { try { overlayMarkerRef.current.setMap(null); } catch (e) { /* noop */ } overlayMarkerRef.current = null; }
    };
  }, [addressMarker, addressText, project?.location?.lat, project?.location?.lng, project?.map_data?.address, project?.name, mapReady]);

  // ===== CustomDrawingController lifecycle (replaces deprecated DrawingManager) =====
  // Build per-mode style options from current selectedColor/selectedOpacity.
  const drawingOptionsForController = useMemo(() => ({
    polygonOptions: { ...mkDrawOpts(selectedColor, selectedOpacity), editable: true },
    rectangleOptions: { ...mkDrawOpts(selectedColor, selectedOpacity), editable: true },
    circleOptions: { ...mkDrawOpts(selectedColor, selectedOpacity), editable: true },
    polylineOptions: { strokeColor: selectedColor, strokeWeight: 3, strokeOpacity: 0.8, editable: true, draggable: true },
    markerOptions: { draggable: true },
  }), [selectedColor, selectedOpacity]);

  // Create controller once when map is ready; tear down on unmount or when readOnly toggles.
  useEffect(() => {
    if (!mapReady || readOnly) return undefined;
    const map = mapRef.current;
    if (!map || drawingManagerRef.current) return undefined;
    const controller = new CustomDrawingController(map, {
      onOverlayComplete: (e) => {
        if (e.cancelled || !e.overlay) { changeDrawingMode(null); return; }
        onOverlayComplete(e);
        changeDrawingMode(null);
      },
      // Surface existing shapes so the controller can offer snap-to-vertex when the
      // user holds Shift while drawing polygons/polylines.
      getExistingShapes: () => (shapesRef.current || []).map(entry => entry.shape).filter(Boolean),
      ...drawingOptionsForController,
    });
    drawingManagerRef.current = controller;
    return () => {
      try { controller.destroy(); } catch (err) { /* noop */ }
      drawingManagerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, readOnly]);

  // Keep controller styling in sync with the colour/opacity picker.
  useEffect(() => {
    if (drawingManagerRef.current?.setOptions) {
      drawingManagerRef.current.setOptions(drawingOptionsForController);
    }
  }, [drawingOptionsForController]);

  // Push drawingMode state changes to the controller.
  useEffect(() => {
    if (drawingManagerRef.current?.setDrawingMode) {
      drawingManagerRef.current.setDrawingMode(drawingMode);
    }
  }, [drawingMode]);

  // Delete key to remove selected shape
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if user is typing in an input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        const sid = selectedShapeIdRef.current;
        if (!sid) return;
        const idx = measurementsRef.current.findIndex(ms => ms.id === sid);
        if (idx === -1) return;
        const entry = shapesRef.current.find(en => en.id === sid);
        if (entry?.shape) { try { entry.shape.setMap(null); } catch(ex){} }
        shapesRef.current = shapesRef.current.filter(en => en.id !== sid);
        const m = measurementsRef.current[idx];
        setMeasurements(prev => prev.filter(ms => ms.id !== sid));
        if (onDeleteMeasurement && m) onDeleteMeasurement(m);
        setSelectedShapeId(null);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // --- Boundaries (NSW Cadastre WMS) overlay ---
  // Renders the AU state-cadastre tiles on top of the Google base layer when
  // boundaries are enabled. Implementation lives in /map/useBoundariesOverlay.
  useBoundariesOverlay({
    mapReady,
    mapRef,
    boundariesEnabled,
    boundariesOpacity,
    boundariesColourIndex,
    capabilities,
    boundariesOverlayRef,
    boundariesStyleRef,
  });

  // --- Per-parcel vector layer (hover highlight + click → Property Info) ---
  // At zoom ≥ 17 fetches viewport polygons from the state's WFS / ArcGIS REST
  // endpoint, renders them through google.maps.Data, and wires click → panel.
  // Implementation lives in /map/useParcelInspect. The hook also handles the
  // drawing-tool clickable toggle so it doesn't intercept shape-creation clicks.
  useParcelInspect({
    mapReady,
    mapRef,
    parcelDataLayerRef,
    parcelFetchSeqRef,
    selectedParcelIdRef,
    boundariesEnabled,
    capabilities,
    drawingMode,
    customTool,
    setPropertyInfo,
    callbacksRef: parcelCallbacksRef,
  });


  // Google Maps style JSON that hides built-in Points-of-Interest (business
  // markers, transit stations, etc.). Applied via `setOptions({ styles })` on
  // map load — works for non-cloud-styled mapIds including DEMO_MAP_ID.
  const POI_STYLES = useMemo(() => ([
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ]), []);

  // --- Map Load ---
  const onMapLoad = useCallback((map) => {
    mapRef.current = map; map.setTilt(45); map.setMapTypeId('satellite');
    try { map.setOptions({ styles: POI_STYLES }); } catch (ex) { /* mapId may override; harmless */ }
    setMapReady(true);
    // Surface the underlying google.maps.Map instance to the parent (used by
    // the HistoricalCompare overlay to sync center/zoom). Best-effort only.
    if (typeof onMapInstance === 'function') {
      try { onMapInstance(map); } catch (ex) { console.warn('onMapInstance handler failed', ex); }
    }
    // Close properties panel and deselect shape when clicking map background
    map.addListener('click', () => {
      const prevId = selectedShapeIdRef.current;
      if (prevId) {
        const prev = shapesRef.current.find(e => e.id === prevId);
        if (prev?.shape?.setOptions) prev.shape.setOptions({ editable: false });
      }
      setSelectedShapeId(null);
    });
    // Save zoom & center when changed (debounced) — only on deliberate user navigation
    map.addListener('mousedown', () => {});
    map.addListener('mouseup', () => {});
    const saveMapPosition = () => {
      if (!onSaveLocation || readOnly) return;
      clearTimeout(saveLocationTimer.current);
      saveLocationTimer.current = setTimeout(() => {
        // Don't save if in drawing mode or custom tool active
        if (drawingManagerRef.current?.getDrawingMode?.()) return;
        const c = map.getCenter();
        const z = map.getZoom();
        if (c) onSaveLocation({ lat: c.lat(), lng: c.lng(), zoom: z });
      }, 3000);
    };
    map.addListener('zoom_changed', saveMapPosition);
    map.addListener('dragend', saveMapPosition);
    // Clear any existing shapes before restoring (handles re-mount / hot reload)
    shapesRef.current.forEach(e => { try { e.shape.setMap(null); } catch(ex) {} });
    shapesRef.current = [];
    if (project?.measurements?.length) restoreMeasurements(map, project.measurements);
  }, [project]);

  // --- Custom tool click handler ---
  const tempRubberRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !customTool) return;
    map.setOptions({ draggableCursor: 'crosshair' });

    const listener = map.addListener('click', (e) => {
      const pt = e.latLng;
      // For single-click tools (marker, text_label, site_*), skip temp marker — go straight to finish
      if (customTool === 'add_marker' || customTool === 'text_label' || customTool.startsWith('site_')) {
        setCustomPoints([pt]);
        setTimeout(() => finishCustomTool([pt]), 50);
        return;
      }
      // Compute newPts OUTSIDE setCustomPoints to avoid React StrictMode double-invocation
      // of the updater function (which previously caused height/angle to be created twice).
      const prev = customPointsRef.current || [];
      const newPts = [...prev, pt];
      const marker = createMarker({ position: pt, map, icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: selectedColor, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 } });
      tempMarkersRef.current.push(marker);
      if (newPts.length >= 2) {
        if (tempLineRef.current) tempLineRef.current.setMap(null);
        tempLineRef.current = new window.google.maps.Polyline({ path: newPts, strokeColor: selectedColor, strokeWeight: 3, strokeOpacity: 1, zIndex: 1000, map });
      }
      setCustomPoints(newPts);
      if (customTool === 'height' && newPts.length === 2) { setTimeout(() => finishCustomTool(newPts), 100); }
      if (customTool === 'angle' && newPts.length === 3) { setTimeout(() => finishCustomTool(newPts), 100); }
    });

    // Rubber-band preview: faint line from the last placed point to the cursor so the
    // user can see what segment will be added on the next click.
    const moveListener = map.addListener('mousemove', (e) => {
      const pts = customPointsRef.current;
      if (!pts || pts.length === 0) {
        if (tempRubberRef.current) { tempRubberRef.current.setMap(null); tempRubberRef.current = null; }
        return;
      }
      const last = pts[pts.length - 1];
      const segment = [last, e.latLng];
      if (!tempRubberRef.current) {
        tempRubberRef.current = new window.google.maps.Polyline({ path: segment, strokeColor: selectedColor, strokeWeight: 2, strokeOpacity: 0.6, zIndex: 999, map, clickable: false });
      } else {
        tempRubberRef.current.setPath(segment);
      }
    });

    customClickRef.current = listener;
    return () => {
      window.google.maps.event.removeListener(listener);
      window.google.maps.event.removeListener(moveListener);
      if (tempRubberRef.current) { try { tempRubberRef.current.setMap(null); } catch (err) { /* noop */ } tempRubberRef.current = null; }
      map.setOptions({ draggableCursor: null });
    };
  }, [customTool, selectedColor]);

  // --- Finish custom tool ---
  function finishCustomTool(pts) {
    if (!pts || pts.length === 0) { clearCustomTemp(); return; }
    const map = mapRef.current;
    const num = shapesRef.current.length + 1;
    let shape, type;

    if (customTool === 'arrow' && pts.length >= 2) {
      shape = new window.google.maps.Polyline({ path: pts, strokeColor: selectedColor, strokeWeight: 3, editable: true, draggable: true, map,
        icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 4, fillColor: selectedColor, fillOpacity: 1, strokeColor: selectedColor }, offset: '100%' }] });
      type = 'arrow';
    } else if (customTool === 'angle' && pts.length === 3) {
      shape = new window.google.maps.Polyline({ path: pts, strokeColor: selectedColor, strokeWeight: 3, editable: true, draggable: true, map });
      type = 'angle';
    } else if (customTool === 'height' && pts.length === 2) {
      shape = new window.google.maps.Polyline({ path: pts, strokeColor: '#EA5455', strokeWeight: 3, strokeOpacity: 0.8, editable: true, draggable: true, map,
        icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3, strokeColor: '#EA5455' }, offset: '0', repeat: '10px' }] });
      type = 'height';
    } else if (customTool === 'perimeter' && pts.length >= 3) {
      shape = new window.google.maps.Polyline({ path: [...pts, pts[0]], strokeColor: selectedColor, strokeWeight: 2, strokeOpacity: 0.8, editable: true, draggable: true, map });
      type = 'perimeter';
    } else if (customTool === 'multi_distance' && pts.length >= 2) {
      shape = new window.google.maps.Polyline({ path: pts, strokeColor: selectedColor, strokeWeight: 3, editable: true, draggable: true, map });
      type = 'multi_distance';
    } else if (customTool === 'text_label' && pts.length === 1) {
      const text = prompt('Enter label text:') || 'Label';
      shape = createMarker({ position: pts[0], draggable: true, map, label: { text, color: selectedColor, fontSize: '13px', fontWeight: 'bold' } });
      type = 'text_label';
    } else if (customTool === 'add_marker' && pts.length === 1) {
      shape = createMarker({ position: pts[0], draggable: true, map });
      type = 'marker';
    } else if (customTool && customTool.startsWith('site_') && pts.length === 1) {
      const kind = customTool;
      const cfg = SITE_LOGISTICS[kind];
      if (cfg) {
        shape = createSiteMarker(kind, { position: pts[0], draggable: true, map });
        type = kind;
      }
    }

    if (shape) {
      const m = buildMeasurement(shape, type, num, selectedColor);
      pushUndo();
      // Store shape reference immediately (not in async callback)
      const tempId = 'temp_' + Date.now().toString();
      shapesRef.current.push({ shape, id: tempId });
      setMeasurements(prev => [...prev, { ...m, id: tempId }]);
      attachListeners(shape, type);
      if (onSaveMeasurement) {
        onSaveMeasurement(m).then(saved => {
          const realId = saved?.id || tempId;
          if (realId !== tempId) {
            const entry = shapesRef.current.find(s => s.id === tempId);
            if (entry) entry.id = realId;
            setMeasurements(prev => prev.map(item => item.id === tempId ? { ...item, id: realId } : item));
          }
        }).catch(() => { /* keep tempId */ });
      }
    }
    clearCustomTemp();
    setCustomTool(null); setCustomPoints([]);
  }

  function clearCustomTemp() {
    tempMarkersRef.current.forEach(m => m.setMap(null)); tempMarkersRef.current = [];
    if (tempLineRef.current) { tempLineRef.current.setMap(null); tempLineRef.current = null; }
  }

  // --- Restore saved measurements ---
  function restoreMeasurements(map, saved) {
    const restoredMeasurements = [];
    const orphanIds = [];
    saved.forEach((m, i) => {
      try {
        const coords = m.coordinates || [];
        if (!coords.length) {
          // No coordinates — ghost entry, mark for cleanup
          orphanIds.push(m.id);
          return;
        }
        let shape = null; const st = m.shape_type || m.measurement_type;
        const clr = m.color || '#7367F0'; const op = m.opacity || 0.12;
        const opts = mkDrawOpts(clr, op);
        if (st === 'circle' && m.radius) { shape = new window.google.maps.Circle({ center: { lat: coords[0][1], lng: coords[0][0] }, radius: m.radius, ...opts, map }); }
        else if (st === 'rectangle' && coords.length === 2 && !m.rotation) { shape = new window.google.maps.Rectangle({ bounds: { south: coords[0][1], west: coords[0][0], north: coords[1][1], east: coords[1][0] }, ...opts, map }); }
        else if (st === 'rectangle' && coords.length === 2 && m.rotation) {
          // Legacy / partial data: a rectangle has only the unrotated NE/SW bounds but
          // a non-zero rotation is recorded. Reconstruct the 4 rotated corners so we
          // can render the shape correctly instead of dropping it as an orphan.
          const G = window.google.maps.geometry.spherical;
          const south = coords[0][1], west = coords[0][0], north = coords[1][1], east = coords[1][0];
          const sw = new window.google.maps.LatLng(south, west);
          const ne = new window.google.maps.LatLng(north, east);
          const center = new window.google.maps.LatLng((south + north) / 2, (west + east) / 2);
          const fullW = G.computeDistanceBetween(sw, new window.google.maps.LatLng(south, east));
          const fullH = G.computeDistanceBetween(sw, new window.google.maps.LatLng(north, west));
          if (fullW > 0.1 && fullH > 0.1) {
            const rad = -(m.rotation) * Math.PI / 180;
            const cosA = Math.cos(rad), sinA = Math.sin(rad);
            const halfL = fullW / 2, halfW = fullH / 2;
            const local = [
              { dx: -halfL, dy:  halfW },
              { dx:  halfL, dy:  halfW },
              { dx:  halfL, dy: -halfW },
              { dx: -halfL, dy: -halfW },
            ];
            const rotated = local.map(({ dx, dy }) => {
              const rx = dx * cosA - dy * sinA;
              const ry = dx * sinA + dy * cosA;
              const eastLL = G.computeOffset(center, Math.abs(rx), rx >= 0 ? 90 : 270);
              return G.computeOffset(new window.google.maps.LatLng(center.lat(), eastLL.lng()), Math.abs(ry), ry >= 0 ? 0 : 180);
            });
            shape = new window.google.maps.Polygon({ paths: rotated, ...opts, map });
          } else {
            shape = new window.google.maps.Rectangle({ bounds: { south, west, north, east }, ...opts, map });
          }
        }
        else if (st === 'rectangle' && coords.length >= 3) { shape = new window.google.maps.Polygon({ paths: coords.map(c => ({ lat: c[1], lng: c[0] })), ...opts, map }); }
        else if (st === 'arrow') { shape = new window.google.maps.Polyline({ path: coords.map(c => ({ lat: c[1], lng: c[0] })), strokeColor: clr, strokeWeight: 3, editable: false, draggable: !m.locked, map, icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 4, fillColor: clr, fillOpacity: 1, strokeColor: clr }, offset: '100%' }] }); }
        else if (st === 'height') { shape = new window.google.maps.Polyline({ path: coords.map(c => ({ lat: c[1], lng: c[0] })), strokeColor: '#EA5455', strokeWeight: 3, editable: false, draggable: !m.locked, map, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3, strokeColor: '#EA5455' }, offset: '0', repeat: '10px' }] }); }
        else if (st === 'polyline' || st === 'distance' || st === 'multi_distance' || st === 'perimeter' || st === 'angle') { shape = new window.google.maps.Polyline({ path: coords.map(c => ({ lat: c[1], lng: c[0] })), strokeColor: clr, strokeWeight: 3, editable: false, draggable: !m.locked, map }); }
        else if (st === 'marker') { shape = createMarker({ position: { lat: coords[0][1], lng: coords[0][0] }, draggable: !m.locked, map }); }
        else if (st === 'text_label') { shape = createMarker({ position: { lat: coords[0][1], lng: coords[0][0] }, draggable: !m.locked, map, label: { text: m.label || 'Label', color: clr, fontSize: '13px', fontWeight: 'bold' } }); }
        else if (st && st.startsWith('site_')) {
          const cfg = SITE_LOGISTICS[st];
          if (cfg) {
            const pos = { lat: coords[0][1], lng: coords[0][0] };
            shape = createSiteMarker(st, { position: pos, draggable: !m.locked, map });
          }
        }
        else if (coords.length >= 3) { shape = new window.google.maps.Polygon({ paths: coords.map(c => ({ lat: c[1], lng: c[0] })), ...opts, map }); }
        if (shape) {
          shapesRef.current.push({ shape, id: m.id });
          // For rotated rectangles restored as polygons, pass 'polygon' so set_at listeners
          // attach for recalc, and also wire the rectangle constraint preserver.
          const isRotatedRect = (st === 'rectangle' && coords.length >= 3);
          attachListeners(shape, isRotatedRect ? 'polygon' : st, m.id);
          if (isRotatedRect) attachRectangleConstraint(shape, m.id);
          // Respect hidden state from backend
          if (m.hidden) { try { shape.setMap(null); } catch(ex) {} }
          restoredMeasurements.push(m);
        } else {
          // Shape failed to create — orphan
          orphanIds.push(m.id);
        }
      } catch (err) { console.warn('Restore:', err); orphanIds.push(m.id); }
    });
    // Clean up orphans from backend
    if (orphanIds.length > 0 && onDeleteMeasurement) {
      orphanIds.forEach(id => { if (id) onDeleteMeasurement({ id }); });
    }
    setMeasurements(restoredMeasurements.map((m, i) => {
      const out = { ...m, description: m.description || '', color: m.color || '#7367F0', opacity: m.opacity ?? 0.12, locked: m.locked || false };
      if (!m.details) { out.details = m.label || ''; const st = m.shape_type || m.measurement_type; out.label = `${(st || 'Shape').charAt(0).toUpperCase() + (st || 'shape').slice(1)} ${i + 1}`; }
      return out;
    }));
  }

  // --- Recalculate measurements on shape change (debounced) ---
  function recalcAll() {
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    recalcTimerRef.current = setTimeout(() => {
      const cur = measurementsRef.current;
      const newMs = shapesRef.current.map(({ shape, id }, i) => {
        const existing = cur.find(p => p.id === id);
        const type = existing?.shape_type || getShapeType(shape);
        const m = buildMeasurement(shape, type, i + 1, existing?.color);
        return { ...m, id, label: existing?.label || m.label, description: existing?.description || '', color: existing?.color || '#7367F0', opacity: existing?.opacity ?? 0.12, locked: existing?.locked || false, hidden: existing?.hidden || false };
      });
      setMeasurements(newMs);
      // Persist updated coordinates to backend for each changed measurement
      newMs.forEach(m => {
        if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) {
          const existing = cur.find(p => p.id === m.id);
          if (!existing) return;
          const coordsChanged = JSON.stringify(existing.coordinates) !== JSON.stringify(m.coordinates);
          const radiusChanged = (existing.radius || 0) !== (m.radius || 0);
          if (coordsChanged || radiusChanged) {
            onUpdateMeasurement(m.id, { coordinates: m.coordinates, value: m.value, details: m.details, unit: m.unit, radius: m.radius });
          }
        }
      });
    }, 150);
  }

  function showShapeTooltip(shape, id, type, eventLatLng) {
    const map = mapRef.current;
    if (!map) return;
    const m = measurementsRef.current.find(p => p.id === id);
    if (!m) return;
    const pos = eventLatLng || getShapePosition(shape, type);
    if (!pos) return;
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
    }
    infoWindowRef.current.setContent(buildTooltipHTML(m));
    infoWindowRef.current.setPosition(pos);
    infoWindowRef.current.open(map);
  }

  function hideShapeTooltip() {
    if (infoWindowRef.current) infoWindowRef.current.close();
  }

  // --- On-shape measurement labels ---

  function updateShapeLabels() {
    const map = mapRef.current;
    if (!map) return;
    // Remove old labels
    labelMarkersRef.current.forEach(lm => lm.setMap(null));
    labelMarkersRef.current = [];
    const G = window.google.maps.geometry.spherical;
    // Create labels for each visible measurement
    measurementsRef.current.forEach(m => {
      if (m.hidden) return;
      const entry = shapesRef.current.find(e => e.id === m.id);
      if (!entry) return;

      // Center area label
      const text = formatShapeLabel(m);
      if (text) {
        const pos = getShapePosition(entry.shape, m.shape_type || m.measurement_type);
        if (pos) {
          labelMarkersRef.current.push(createMarker({
            position: pos, map,
            label: { text, color: '#FFFFFF', fontSize: '11px', fontWeight: '700', fontFamily: 'Public Sans, sans-serif', className: 'shape-measurement-label' },
            clickable: false, zIndex: 1
          }));
        }
      }

      // Edge labels for rectangles, area polygons, and line-type shapes
      const st = m.shape_type || m.measurement_type || '';
      const isAreaShape = (st === 'rectangle' || st === 'polygon') && m.measurement_type === 'area';
      const isLineShape = st === 'polyline' || st === 'arrow' || st === 'multi_distance' || st === 'perimeter';
      if (isAreaShape || isLineShape) {
        let path = null;
        if (entry.shape.getPath) {
          path = entry.shape.getPath();
        } else if (entry.shape.getBounds) {
          const b = entry.shape.getBounds();
          if (b) {
            const ne = b.getNorthEast(), sw = b.getSouthWest();
            path = new window.google.maps.MVCArray([
              new window.google.maps.LatLng(sw.lat(), sw.lng()),
              new window.google.maps.LatLng(sw.lat(), ne.lng()),
              new window.google.maps.LatLng(ne.lat(), ne.lng()),
              new window.google.maps.LatLng(ne.lat(), sw.lng()),
            ]);
          }
        }
        if (path && path.getLength() >= 2) {
          const n = path.getLength();
          const segCount = isAreaShape ? n : n - 1; // Lines don't close, polygons do
          for (let i = 0; i < segCount; i++) {
            const p1 = path.getAt(i);
            const p2 = path.getAt((i + 1) % n);
            const dist = G.computeDistanceBetween(p1, p2);
            const midLat = (p1.lat() + p2.lat()) / 2;
            const midLng = (p1.lng() + p2.lng()) / 2;
            const edgeText = dist >= 1000 ? `${(dist / 1000).toFixed(2)} km` : `${dist.toFixed(1)} m`;
            labelMarkersRef.current.push(createMarker({
              position: { lat: midLat, lng: midLng }, map,
              label: { text: edgeText, color: '#FFFFFF', fontSize: '9px', fontWeight: '600', fontFamily: 'Public Sans, sans-serif', className: 'shape-edge-label' },
              clickable: false, zIndex: 2
            }));
          }
        }
      }
    });
  }

  // For rotated rectangles (stored as 4-corner polygons), maintain orthogonality and
  // the recorded rotation angle when the user drags a vertex. The diagonal-opposite
  // corner stays anchored throughout the drag; new length/width are derived from the
  // current dragged corner projected onto the rectangle's local axes.
  //
  // Important details:
  //   • `dragStartCorners` is captured on the first `set_at` of a burst and reused
  //     for every subsequent `set_at` in the same drag, so the anchor never drifts
  //     mid-drag. It's released ~250ms after the last `set_at` (drag finished).
  //   • A `dragging` flag (set by `dragstart` / `dragend`) skips the rebuild during
  //     a whole-shape drag — those events translate every vertex uniformly and would
  //     otherwise be misread as a resize against a stale opposite corner.
  //   • `insert_at` is suppressed: Google Maps polygons let users click an edge
  //     midpoint to add a 5th vertex; for a rectangle we silently remove it.
  function attachRectangleConstraint(shape, measurementId) {
    const g = window.google.maps;
    const G = g.geometry.spherical;
    const path = shape.getPath();
    if (!path || path.getLength() !== 4) return;
    let prevCorners = [];
    for (let i = 0; i < 4; i++) prevCorners.push(path.getAt(i));
    let dragStartCorners = null;
    let pendingTimer = null;
    let suppress = false;
    let dragging = false;

    const finalizeBurst = () => {
      const next = [];
      for (let i = 0; i < 4; i++) next.push(path.getAt(i));
      prevCorners = next;
      dragStartCorners = null;
      pendingTimer = null;
    };

    g.event.addListener(shape, 'dragstart', () => { dragging = true; });
    g.event.addListener(shape, 'dragend', () => {
      dragging = false;
      if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
      finalizeBurst();
    });
    g.event.addListener(path, 'insert_at', (idx) => {
      // Edge-midpoint click — block by removing the inserted vertex immediately.
      if (suppress) return;
      if (path.getLength() > 4) {
        suppress = true;
        try { path.removeAt(idx); } catch (ex) {}
        suppress = false;
      }
    });
    g.event.addListener(path, 'set_at', (idx) => {
      if (suppress || dragging) return;
      if (path.getLength() !== 4) return;
      // Capture the anchor on the first set_at of this drag burst and keep using it
      // until the burst finishes, so the opposite corner never drifts mid-drag.
      // Also push the pre-drag state to the undo stack so the user can undo this
      // vertex-drag resize as a single step.
      if (!dragStartCorners) {
        dragStartCorners = prevCorners.slice();
        pushUndo();
      }
      const m = measurementsRef.current.find(x => x.id === measurementId);
      const rotationDeg = (m && typeof m.rotation === 'number') ? m.rotation : 0;
      const oppositeIdx = (idx + 2) % 4;
      const fixedCorner = dragStartCorners[oppositeIdx];
      const newCorner = path.getAt(idx);
      if (!fixedCorner || !newCorner) return;
      // Center is midpoint of the diagonal between the moved corner and its anchor.
      const center = new g.LatLng(
        (fixedCorner.lat() + newCorner.lat()) / 2,
        (fixedCorner.lng() + newCorner.lng()) / 2
      );
      // Signed (east, north) offset (in meters) of the moved corner from the center.
      const dEast = G.computeDistanceBetween(center, new g.LatLng(center.lat(), newCorner.lng()))
        * (newCorner.lng() >= center.lng() ? 1 : -1);
      const dNorth = G.computeDistanceBetween(center, new g.LatLng(newCorner.lat(), center.lng()))
        * (newCorner.lat() >= center.lat() ? 1 : -1);
      const rad = -rotationDeg * Math.PI / 180;
      const cosA = Math.cos(rad), sinA = Math.sin(rad);
      // Magnitude of the rectangle's half-axes recovered from the dragged-corner offset
      // (the sign depends on which corner index was dragged, but Math.abs collapses it).
      const newHalfL = Math.max(Math.abs(dEast * cosA + dNorth * sinA), 0.1);
      const newHalfW = Math.max(Math.abs(-dEast * sinA + dNorth * cosA), 0.1);
      const cornersLocal = [
        { dx: -newHalfL, dy:  newHalfW }, // 0 TL
        { dx:  newHalfL, dy:  newHalfW }, // 1 TR
        { dx:  newHalfL, dy: -newHalfW }, // 2 BR
        { dx: -newHalfL, dy: -newHalfW }, // 3 BL
      ];
      const rebuilt = cornersLocal.map(({ dx, dy }) => {
        const rx = dx * cosA - dy * sinA;
        const ry = dx * sinA + dy * cosA;
        const east = G.computeOffset(center, Math.abs(rx), rx >= 0 ? 90 : 270);
        return G.computeOffset(
          new g.LatLng(center.lat(), east.lng()),
          Math.abs(ry), ry >= 0 ? 0 : 180
        );
      });
      suppress = true;
      for (let i = 0; i < 4; i++) path.setAt(i, rebuilt[i]);
      suppress = false;
      // Defer prevCorners refresh until the drag burst settles (250ms after the last
      // set_at). Updating it every frame would let mid-drag drift accumulate.
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = setTimeout(finalizeBurst, 250);
    });
  }

  // Predicate kept for symmetry with earlier non-editable approach; with the burst-
  // anchored constraint in place, rotated rectangles ARE editable again.
  function isRotatedRectanglePolygon() { return false; }

  // Returns the first user-drawn shape entry that geometrically contains the
  // given LatLng — used to give shapes click-priority over the cadastre parcel
  // data layer (both are clickable; without this the parcel always wins when
  // it sits underneath a user shape).
  function findUserShapeAt(latLng) {
    if (!latLng || !window.google?.maps) return null;
    const G = window.google.maps;
    for (const entry of shapesRef.current) {
      const shape = entry?.shape;
      if (!shape || !shape.getMap || !shape.getMap()) continue;
      try {
        if (shape instanceof G.Rectangle) {
          const b = shape.getBounds();
          if (b && b.contains(latLng)) return entry;
        } else if (shape instanceof G.Circle) {
          const c = shape.getCenter();
          const r = shape.getRadius();
          if (c && r > 0) {
            const d = G.geometry.spherical.computeDistanceBetween(c, latLng);
            if (d <= r) return entry;
          }
        } else if (shape instanceof G.Polygon) {
          if (G.geometry.poly.containsLocation(latLng, shape)) return entry;
        }
        // Markers / polylines / labels intentionally skipped — they're hit by
        // Google's own marker click handling, not by the polygonal hit test.
      } catch { /* shape may be torn down between event dispatch and hit-test */ }
    }
    return null;
  }

  // Symmetric to selectShapeById — clears the active shape selection AND its
  // editable vertex handles. Use this whenever something else (e.g. clicking
  // a parcel) takes selection focus.
  function deselectAllShapes() {
    const prevId = selectedShapeIdRef.current;
    if (prevId) {
      const prev = shapesRef.current.find((entry) => entry.id === prevId);
      if (prev?.shape?.setOptions) {
        try { prev.shape.setOptions({ editable: false }); } catch { /* shape may be torn down */ }
      }
    }
    setSelectedShapeId(null);
  }

  function selectShapeById(sid) {
    if (!sid) return;
    hideShapeTooltip();
    // Deselect previous shape
    const prevId = selectedShapeIdRef.current;
    if (prevId && prevId !== sid) {
      const prev = shapesRef.current.find((entry) => entry.id === prevId);
      if (prev?.shape?.setOptions) prev.shape.setOptions({ editable: false });
    }
    const entry = shapesRef.current.find((e) => e.id === sid);
    if (entry?.shape?.setOptions && !isRotatedRectanglePolygon(entry.shape, sid)) {
      entry.shape.setOptions({ editable: true });
    }
    setSelectedShapeId(sid);
  }

  // Keep useParcelInspect's click handler closing over the *latest* versions of
  // these three helpers. Assigning on every render is cheap and avoids re-
  // binding the Google Maps `click` listener every time a measurement is added.
  parcelCallbacksRef.current.findUserShapeAt = findUserShapeAt;
  parcelCallbacksRef.current.selectShapeById = selectShapeById;
  parcelCallbacksRef.current.deselectAllShapes = deselectAllShapes;

  function attachListeners(shape, type, explicitId) {
    if (type === 'circle') { window.google.maps.event.addListener(shape, 'radius_changed', recalcAll); window.google.maps.event.addListener(shape, 'center_changed', recalcAll); }
    else if (type === 'rectangle') { window.google.maps.event.addListener(shape, 'bounds_changed', recalcAll); }
    else if (isMarker(shape)) {
      window.google.maps.event.addListener(shape, 'dragend', recalcAll);
    }
    else if (shape.getPath) { const p = shape.getPath(); ['set_at', 'insert_at', 'remove_at'].forEach(e => window.google.maps.event.addListener(p, e, recalcAll)); window.google.maps.event.addListener(shape, 'dragend', recalcAll); }
    // Hover tooltip + click opens properties panel
    const resolveId = () => explicitId || shapesRef.current.find(e => e.shape === shape)?.id;
    window.google.maps.event.addListener(shape, 'mouseover', (e) => {
      const sid = resolveId();
      if (sid) showShapeTooltip(shape, sid, type, e?.latLng);
    });
    window.google.maps.event.addListener(shape, 'click', () => {
      const sid = resolveId();
      if (sid) selectShapeById(sid);
    });
    window.google.maps.event.addListener(shape, 'mouseout', hideShapeTooltip);
  }

  function onOverlayComplete(e) {
    const shape = e.overlay, type = e.type;
    if (shapesRef.current.some(s => s.shape === shape)) return;
    const num = shapesRef.current.length + 1;
    const m = buildMeasurement(shape, type, num, selectedColor);
    if (shape.setOptions) shape.setOptions(mkDrawOpts(selectedColor, selectedOpacity));
    pushUndo();
    // Generate a temporary ID immediately so the shape is tracked from the start
    const tempId = 'temp_' + Date.now().toString();
    shapesRef.current.push({ shape, id: tempId });
    setMeasurements(prev => [...prev, { ...m, id: tempId }]);
    attachListeners(shape, type);
    // Save to backend and update ID
    if (onSaveMeasurement) {
      onSaveMeasurement(m).then(saved => {
        const realId = saved?.id || tempId;
        if (realId !== tempId) {
          // Update the ID in shapesRef and measurements
          const entry = shapesRef.current.find(s => s.id === tempId);
          if (entry) entry.id = realId;
          setMeasurements(prev => prev.map(item => item.id === tempId ? { ...item, id: realId } : item));
        }
      }).catch(() => { /* keep tempId */ });
    }
  }

  // --- Undo/Redo ---
  function pushUndo() { setUndoStack(prev => [...prev, measurements.map(m => ({ ...m }))]); setRedoStack([]); }
  function disposeAllShapes() {
    const g = window.google?.maps;
    shapesRef.current.forEach(e => {
      try {
        if (g) {
          g.event.clearInstanceListeners(e.shape);
          const p = e.shape.getPath?.();
          if (p) g.event.clearInstanceListeners(p);
          // Legacy: dispose any orphan __craneCircle attached by older sessions
          // before crane swing-radius was removed. Once all live sessions roll
          // over this becomes dead defensive code, but it's cheap.
          if (e.shape.__craneCircle) g.event.clearInstanceListeners(e.shape.__craneCircle);
        }
      } catch (ex) {}
      try { e.shape.setMap(null); } catch (ex) {}
      try { e.shape.__craneCircle?.setMap?.(null); } catch (ex) {}
    });
    shapesRef.current = [];
  }
  function undo() {
    if (undoStack.length === 0) return;
    setRedoStack(prev => [...prev, measurements.map(m => ({ ...m }))]);
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(s => s.slice(0, -1));
    disposeAllShapes();
    setMeasurements(prev);
    if (mapRef.current && prev.length > 0) restoreMeasurements(mapRef.current, prev);
  }
  function redo() {
    if (redoStack.length === 0) return;
    setUndoStack(prev => [...prev, measurements.map(m => ({ ...m }))]);
    const next = redoStack[redoStack.length - 1];
    setRedoStack(s => s.slice(0, -1));
    disposeAllShapes();
    setMeasurements(next);
    if (mapRef.current && next.length > 0) restoreMeasurements(mapRef.current, next);
  }

  // --- Measurement Actions ---
  function findShapeById(id) {
    return shapesRef.current.find(e => e.id === id);
  }

  function confirmDelete() {
    if (deleteIdx === null) return;
    const m = measurements[deleteIdx];
    if (!m) { setDeleteIdx(null); return; }
    pushUndo();
    // Remove ALL shapes matching this measurement's ID from the map
    const toRemove = shapesRef.current.filter(e => e.id === m.id);
    toRemove.forEach(e => {
      // Legacy: dispose any orphan __craneCircle attached on older site_crane
      // markers — fixes the "ghost circle stays after delete" bug for shapes
      // created before crane swing-radius was removed as a feature.
      try { e.shape.__craneCircle?.setMap?.(null); } catch(ex) {}
      try { e.shape.setMap(null); } catch(ex) {}
    });
    shapesRef.current = shapesRef.current.filter(e => e.id !== m.id);
    // Update UI state
    setMeasurements(prev => prev.filter((_, i) => i !== deleteIdx));
    if (onDeleteMeasurement && m) onDeleteMeasurement(m);
    setDeleteIdx(null);
  }

  function duplicateShape(idx) {
    const m = measurements[idx]; if (!m) return;
    const entry = findShapeById(m.id); if (!entry) return;
    const map = mapRef.current; const coords = m.coordinates || [];
    const offset = 0.0002;
    let newShape;
    const clr = m.color || '#7367F0'; const op = m.opacity || 0.12;
    const offsetCoords = coords.map(c => [c[0] + offset, c[1] + offset]);
    if (m.shape_type === 'circle' && m.radius) {
      newShape = new window.google.maps.Circle({ center: { lat: coords[0][1] + offset, lng: coords[0][0] + offset }, radius: m.radius, ...mkDrawOpts(clr, op), map });
    } else if (m.shape_type === 'rectangle' && coords.length === 2) {
      newShape = new window.google.maps.Rectangle({ bounds: { south: coords[0][1] + offset, west: coords[0][0] + offset, north: coords[1][1] + offset, east: coords[1][0] + offset }, ...mkDrawOpts(clr, op), map });
    } else if (m.shape_type === 'marker' || m.shape_type === 'text_label') {
      newShape = createMarker({ position: { lat: coords[0][1] + offset, lng: coords[0][0] + offset }, draggable: true, map });
    } else if (coords.length >= 2) {
      newShape = new window.google.maps.Polyline({ path: offsetCoords.map(c => ({ lat: c[1], lng: c[0] })), strokeColor: clr, strokeWeight: 2, editable: true, draggable: true, map });
    } else if (coords.length >= 3) {
      newShape = new window.google.maps.Polygon({ paths: offsetCoords.map(c => ({ lat: c[1], lng: c[0] })), ...mkDrawOpts(clr, op), map });
    }
    if (newShape) {
      pushUndo();
      // Build a proper measurement with offset coordinates for the copy
      const newM = {
        ...m,
        coordinates: offsetCoords,
        label: `${m.label} (copy)`,
        locked: false,
        color: clr,
        opacity: op,
      };
      delete newM.id; // let backend generate new ID
      const tempId = 'temp_' + Date.now().toString();
      shapesRef.current.push({ shape: newShape, id: tempId });
      setMeasurements(prev => [...prev, { ...newM, id: tempId }]);
      attachListeners(newShape, m.shape_type);
      // Save to backend
      if (onSaveMeasurement) {
        onSaveMeasurement(newM).then(saved => {
          const realId = saved?.id || tempId;
          if (realId !== tempId) {
            const se = shapesRef.current.find(s => s.id === tempId);
            if (se) se.id = realId;
            setMeasurements(prev => prev.map(item => item.id === tempId ? { ...item, id: realId } : item));
          }
        }).catch(() => {});
      }
    }
  }

  function toggleLock(idx) {
    const m = measurements[idx]; if (!m) return;
    const entry = findShapeById(m.id);
    setMeasurements(prev => prev.map((item, i) => i === idx ? { ...item, locked: !item.locked } : item));
    if (!entry) return;
    const isLocked = m.locked;
    const shape = entry.shape;
    try { if (shape.setOptions) shape.setOptions({ editable: isLocked, draggable: isLocked }); } catch(e) {}
    try { if (shape.setEditable) shape.setEditable(isLocked); } catch(e) {}
    try { if (shape.setDraggable) shape.setDraggable(isLocked); } catch(e) {}
  }

  function updateColor(idx, color) {
    const m = measurements[idx]; if (!m) return;
    // Always update UI first
    setMeasurements(prev => prev.map((item, i) => i === idx ? { ...item, color } : item));
    // Update every shape with this ID on the map
    shapesRef.current.forEach(entry => {
      if (entry.id === m.id && entry.shape) {
        try { entry.shape.setOptions({ fillColor: color, strokeColor: color }); } catch(e) {}
      }
    });
    // Persist to backend
    if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) {
      onUpdateMeasurement(m.id, { color });
    }
  }

  function updateOpacity(idx, opacity) {
    const m = measurements[idx]; if (!m) return;
    setMeasurements(prev => prev.map((item, i) => i === idx ? { ...item, opacity } : item));
    shapesRef.current.forEach(entry => {
      if (entry.id === m.id && entry.shape) {
        try { entry.shape.setOptions({ fillOpacity: opacity }); } catch(e) {}
      }
    });
    // Persist to backend
    if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) {
      onUpdateMeasurement(m.id, { opacity });
    }
  }

  function toggleVis(idx) {
    const m = measurements[idx]; if (!m) return;
    const entry = findShapeById(m.id);
    const newHidden = !m.hidden;
    if (entry) {
      try { entry.shape.setMap(newHidden ? null : mapRef.current); } catch(e) {}
    }
    setMeasurements(prev => prev.map((item, i) => i === idx ? { ...item, hidden: newHidden } : item));
    if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) {
      onUpdateMeasurement(m.id, { hidden: newHidden });
    }
  }
  function showAll() {
    shapesRef.current.forEach(e => { try { e.shape.setMap(mapRef.current); } catch(ex) {} });
    setMeasurements(prev => prev.map(m => ({ ...m, hidden: false })));
    measurements.forEach(m => {
      if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) onUpdateMeasurement(m.id, { hidden: false });
    });
  }
  function hideAll() {
    shapesRef.current.forEach(e => { try { e.shape.setMap(null); } catch(ex) {} });
    setMeasurements(prev => prev.map(m => ({ ...m, hidden: true })));
    measurements.forEach(m => {
      if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) onUpdateMeasurement(m.id, { hidden: true });
    });
  }
  function handleSaveEdit(idx, title, desc) {
    const m = measurements[idx];
    setMeasurements(prev => prev.map((item, i) => i === idx ? { ...item, label: title, description: desc } : item));
    if (onUpdateMeasurement && m?.id && !m.id.startsWith('temp_')) {
      onUpdateMeasurement(m.id, { label: title, description: desc });
    }
  }

  function handleReplaceShape(measurementId, newCorners, extraUpdates) {
    const map = mapRef.current;
    if (!map) return;
    const entry = shapesRef.current.find(e => e.id === measurementId);
    if (!entry) return;
    const m = measurements.find(ms => ms.id === measurementId);
    if (!m) return;
    // Record this rotation/resize as an undoable step so Ctrl+Z reverts the geometry
    // change instead of removing the entire shape.
    pushUndo();
    // Remove old shape and clear its listeners to avoid stale handlers from the
    // pre-replacement shape still firing against a no-longer-mounted overlay.
    try { window.google.maps.event.clearInstanceListeners(entry.shape); } catch (ex) {}
    try {
      const oldPath = entry.shape.getPath?.();
      if (oldPath) window.google.maps.event.clearInstanceListeners(oldPath);
    } catch (ex) {}
    try { entry.shape.setMap(null); } catch (ex) {}
    // Create new polygon from corners
    const clr = m.color || '#7367F0';
    const op = m.opacity || 0.12;
    const newShape = new window.google.maps.Polygon({
      paths: newCorners,
      ...mkDrawOpts(clr, op),
      map
    });
    // Replace in shapesRef
    entry.shape = newShape;
    // Attach listeners. We keep the original shape_type ('rectangle') so the panel
    // continues to show length/width fields. The rectangle constraint preserves
    // orthogonality during vertex drags.
    const originalType = m.shape_type || 'polygon';
    attachListeners(newShape, originalType === 'rectangle' ? 'polygon' : originalType, measurementId);
    if (originalType === 'rectangle') {
      attachRectangleConstraint(newShape, measurementId);
    }
    // Recalculate measurement values
    const area = window.google.maps.geometry.spherical.computeArea(newShape.getPath());
    const perim = window.google.maps.geometry.spherical.computeLength(newShape.getPath());
    const coords = [];
    const path = newShape.getPath();
    for (let i = 0; i < path.getLength(); i++) {
      coords.push([path.getAt(i).lng(), path.getAt(i).lat()]);
    }
    const G = window.google.maps.geometry.spherical;
    const p0 = path.getAt(0), p1 = path.getAt(1), p3 = path.getAt(3);
    const edgeL = G.computeDistanceBetween(p0, p1);
    const edgeW = G.computeDistanceBetween(p0, p3);
    const fmtD = (v) => v >= 1000 ? `${(v/1000).toFixed(2)} km` : `${v.toFixed(2)} m`;
    const details = `Type: roof_area\nArea: ${area.toFixed(2)} m\u00B2\nW: ${fmtD(edgeL)} x H: ${fmtD(edgeW)}`;
    const updates = {
      value: area, coordinates: coords, details,
      ...extraUpdates
    };
    setMeasurements(prev => prev.map(item => item.id === measurementId ? { ...item, ...updates } : item));
    if (onUpdateMeasurement && measurementId && !measurementId.startsWith('temp_')) {
      onUpdateMeasurement(measurementId, updates);
    }
  }

  function handleSnapshot() {
    const c = mapContainerRef.current; if (!c) return;
    // Hide all UI panels/overlays for clean snapshot
    const uiElements = c.querySelectorAll('[data-testid="map-top-bar"], [data-testid="shape-properties-panel"], [data-testid="measurement-panel"], [data-testid="map-style-switcher"], [data-testid="map-instructions"]');
    uiElements.forEach(el => el.style.display = 'none');
    // Also deselect shape to remove edit dots
    const prevSelected = selectedShapeIdRef.current;
    if (prevSelected) {
      const prev = shapesRef.current.find(e => e.id === prevSelected);
      if (prev?.shape?.setOptions) prev.shape.setOptions({ editable: false });
    }
    setTimeout(() => {
      // Strip Google Maps inline text-stroke styles that cause shadow in html2canvas
      const labelEls = c.querySelectorAll('.shape-measurement-label, .shape-edge-label');
      const origStyles = [];
      labelEls.forEach(el => {
        const parent = el.closest('div[style]');
        if (parent) {
          origStyles.push({ el: parent, style: parent.getAttribute('style') });
          parent.style.webkitTextStroke = '0';
          parent.style.textShadow = 'none';
          parent.style.paintOrder = 'normal';
        }
      });
      html2canvas(c, { useCORS: true, allowTaint: true, scale: 1.5, logging: false, imageTimeout: 5000 }).then(srcCanvas => {
        // Restore UI / shape state regardless of how the composite goes.
        const restoreUI = () => {
          uiElements.forEach(el => el.style.display = '');
          origStyles.forEach(({ el, style }) => el.setAttribute('style', style));
          if (prevSelected) {
            const prev = shapesRef.current.find(e => e.id === prevSelected);
            if (prev?.shape?.setOptions) prev.shape.setOptions({ editable: true });
          }
        };
        const triggerDownload = (canvas) => {
          // Draw the bottom-right project watermark BEFORE downloading. Same
          // visual language as the exported GIF (semi-transparent dark box,
          // 3 lines of metadata) so snapshots and time-lapse GIFs are
          // visually consistent when shared with clients.
          try {
            const ctxLocal = canvas.getContext('2d');
            const lines = [];
            if (project?.name) lines.push(String(project.name).slice(0, 60));
            const addr = project?.map_data?.address || project?.address;
            if (addr) lines.push(String(addr).slice(0, 80));
            lines.push('RoofMate');
            // Scale font with image width so it reads at any export size.
            const fontSize = Math.max(13, Math.min(22, Math.round(canvas.width * 0.013)));
            const lineGap = Math.round(fontSize * 0.35);
            ctxLocal.save();
            ctxLocal.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
            ctxLocal.textBaseline = 'top';
            // Measure widest line for the backing box.
            const widths = lines.map((t) => ctxLocal.measureText(t).width);
            const boxW = Math.ceil(Math.max(...widths)) + 22;
            const boxH = lines.length * fontSize + (lines.length - 1) * lineGap + 18;
            const margin = Math.round(canvas.width * 0.012);
            const x0 = canvas.width - margin - boxW;
            const y0 = canvas.height - margin - boxH;
            // Rounded semi-transparent backing.
            ctxLocal.fillStyle = 'rgba(0,0,0,0.55)';
            const rx = 10;
            ctxLocal.beginPath();
            ctxLocal.moveTo(x0 + rx, y0);
            ctxLocal.arcTo(x0 + boxW, y0, x0 + boxW, y0 + boxH, rx);
            ctxLocal.arcTo(x0 + boxW, y0 + boxH, x0, y0 + boxH, rx);
            ctxLocal.arcTo(x0, y0 + boxH, x0, y0, rx);
            ctxLocal.arcTo(x0, y0, x0 + boxW, y0, rx);
            ctxLocal.closePath();
            ctxLocal.fill();
            // Lines themselves — brand line dimmer to mirror GIF watermark hierarchy.
            let cy = y0 + 9;
            lines.forEach((line) => {
              const isBrand = line === 'RoofMate';
              ctxLocal.fillStyle = isBrand ? 'rgba(225,225,235,0.85)' : 'rgba(255,255,255,0.95)';
              ctxLocal.fillText(line, x0 + 11, cy);
              cy += fontSize + lineGap;
            });
            ctxLocal.restore();
          } catch (wmErr) {
            console.warn('snapshot-watermark: draw failed, downloading without watermark:', wmErr);
          }
          const link = document.createElement('a');
          link.download = `roofmate-${project?.name || 'snapshot'}-${Date.now()}.jpg`;
          link.href = canvas.toDataURL('image/jpeg', 0.85);
          link.click();
          restoreUI();
        };
        // Copy the html2canvas output onto a FRESH offscreen canvas so any
        // residual transform/clip/alpha/composite state from html2canvas's
        // internal context is gone. drawImage with the brand logo then lands
        // exactly where we expect — this was the root cause of the missing
        // logo in iterations 20-21 (drawImage ran but painted invisibly).
        const out = document.createElement('canvas');
        out.width = srcCanvas.width;
        out.height = srcCanvas.height;
        const ctx = out.getContext('2d');
        ctx.drawImage(srcCanvas, 0, 0);

        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = () => {
          try {
            const targetW = Math.max(120, Math.min(260, Math.round(out.width * 0.10)));
            const ratio = targetW / logoImg.width;
            const targetH = Math.round(logoImg.height * ratio);
            const padX = 20, padY = 16;
            // Defensive: reset any state in case the offscreen ctx inherited
            // anything weird from the platform (paranoid but cheap).
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
            // Rounded semi-transparent backing for legibility.
            ctx.save();
            ctx.fillStyle = 'rgba(0,0,0,0.45)';
            const rx = 10;
            const bx0 = padX - 8, by0 = padY - 6;
            const bw = targetW + 16, bh = targetH + 12;
            ctx.beginPath();
            ctx.moveTo(bx0 + rx, by0);
            ctx.arcTo(bx0 + bw, by0, bx0 + bw, by0 + bh, rx);
            ctx.arcTo(bx0 + bw, by0 + bh, bx0, by0 + bh, rx);
            ctx.arcTo(bx0, by0 + bh, bx0, by0, rx);
            ctx.arcTo(bx0, by0, bx0 + bw, by0, rx);
            ctx.closePath();
            ctx.fill();
            ctx.drawImage(logoImg, padX, padY, targetW, targetH);
            ctx.restore();
          } catch (drawErr) {
            console.warn('snapshot-logo: drawImage failed, downloading without logo:', drawErr);
          }
          triggerDownload(out);
        };
        logoImg.onerror = (err) => {
          console.warn('snapshot-logo: /logo-full-white.png failed to load — downloading without logo', err);
          triggerDownload(out);
        };
        logoImg.src = '/logo-full-white.png';
      }).catch((htmlCanvasErr) => {
        console.warn('snapshot: html2canvas failed', htmlCanvasErr);
        uiElements.forEach(el => el.style.display = '');
        origStyles.forEach(({ el, style }) => el.setAttribute('style', style));
        if (prevSelected) {
          const prev = shapesRef.current.find(e => e.id === prevSelected);
          if (prev?.shape?.setOptions) prev.shape.setOptions({ editable: true });
        }
      });
    }, 100);
  }

  // Apply the active map type — when the user is on Satellite AND has saved a
  // historical date, register a Wayback ImageMapType into `map.mapTypes` and
  // switch to it (Wayback imagery is flat so we also kill the 45° tilt).
  // Otherwise fall back to the requested base type. This single helper is
  // used both by user clicks (changeMapType) and by reactive effects below
  // so the resolution rule lives in one place.
  // Resolve the tilt to apply for a given (base map type, view mode) pair.
  // Bird's-eye 45° tilt only makes sense on satellite/hybrid — Google's SDK
  // silently no-ops it on roadmap/terrain anyway, but we keep the rule
  // explicit so the ViewModePill state and the actual tilt agree.
  const tiltForView = useCallback((baseId, mode) => {
    if (mode !== '45') return 0;
    return (baseId === 'satellite' || baseId === 'hybrid') ? 45 : 0;
  }, []);

  const applyMapType = useCallback((id) => {
    const map = mapRef.current;
    if (!map) return;
    if (id === 'satellite' && imageryDate?.release_id) {
      const layer = buildWaybackLayer(imageryDate);
      if (layer) {
        const key = `wayback_${imageryDate.release_id}`;
        try {
          if (!map.mapTypes.get(key)) map.mapTypes.set(key, layer);
          map.setMapTypeId(key);
          // Honour the user's chosen viewMode — Wayback tiles are 2D
          // imagery but the vector camera can still tilt them for a
          // perspective view, so we forward the same tilt rule used for
          // live satellite. (Previously we hard-coded 0 which broke the
          // 45° button as soon as a historical date was selected.)
          if (typeof map.moveCamera === 'function') {
            map.moveCamera({ tilt: tiltForView(id, viewMode), heading: map.getHeading?.() ?? 0 });
          } else {
            map.setTilt(tiltForView(id, viewMode));
          }
          // Clamp the map's maxZoom so the user can't zoom past the
          // tile range — without this, zoom 21+ shows a blank grey map
          // because Wayback returns CORB-blocked 404s up there.
          map.setOptions({ maxZoom: WAYBACK_MAX_ZOOM });
          if ((map.getZoom() || 0) > WAYBACK_MAX_ZOOM) map.setZoom(WAYBACK_MAX_ZOOM);
          return;
        } catch { /* fall through to plain satellite */ }
      }
    }
    try {
      map.setMapTypeId(id);
      // Route through applyTilt — see comment on applyTilt for why setTilt
      // alone is unreliable on vector mapIds.
      const map2 = mapRef.current;
      if (map2) {
        try {
          if (typeof map2.moveCamera === 'function') {
            map2.moveCamera({ tilt: tiltForView(id, viewMode), heading: map2.getHeading?.() ?? 0 });
          } else {
            map2.setTilt(tiltForView(id, viewMode));
          }
        } catch { /* ignore */ }
      }
      // Restore Google's default max zoom when we're not on a Wayback layer.
      map.setOptions({ maxZoom: undefined });
    } catch { /* SDK may throw if map disposed */ }
  }, [imageryDate, buildWaybackLayer, tiltForView, viewMode]);

  function changeMapType(id) { setMapType(id); applyMapType(id); }

  // Apply tilt to the underlying vector map. setTilt() alone is unreliable
  // on vector mapIds (the camera ignores it after the initial load), so we
  // route through moveCamera() which is the documented public API for
  // vector-mode camera updates. Heading is preserved.
  const applyTilt = useCallback((tilt) => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (typeof map.moveCamera === 'function') {
        map.moveCamera({ tilt, heading: map.getHeading?.() ?? 0 });
      } else {
        map.setTilt(tilt);
      }
    } catch { /* SDK unavailable / disposed — ignore */ }
  }, []);

  // View-mode handler — '2d' / '45' toggle tilt; '360' pops the Street View
  // modal without altering the underlying base map. When the user picks 45°
  // we also auto-zoom to 18 (Google won't render tilted aerial / 3D
  // buildings below that zoom level, so the click would otherwise look
  // like a no-op).
  const changeViewMode = useCallback((next) => {
    if (next === '360') {
      setStreetViewOpen(true);
      return;
    }
    setViewMode(next);
    const map = mapRef.current;
    if (!map) return;
    if (next === '45') {
      // Auto-zoom to the minimum zoom Google needs to render tilt.
      try {
        if ((map.getZoom() || 0) < 18) map.setZoom(18);
      } catch { /* ignore */ }
    }
    applyTilt(tiltForView(mapType, next));
  }, [mapType, tiltForView, applyTilt]);

  // Re-apply whenever the date selection changes, the tile template arrives,
  // or the map becomes ready — keeps the displayed imagery in lock-step with
  // both user state and async tile-template loading.
  useEffect(() => {
    if (!mapReady) return;
    applyMapType(mapType);
  }, [mapReady, imageryDate, waybackTileTemplate, applyMapType, mapType]);

  function changeDrawingMode(mode) { setDrawingMode(mode); setCustomTool(null); setCustomPoints([]); clearCustomTemp(); if (drawingManagerRef.current) drawingManagerRef.current.setDrawingMode(mode); }
  function activateCustomTool(tool) { setCustomTool(tool); setDrawingMode(null); setCustomPoints([]); clearCustomTemp(); if (drawingManagerRef.current) drawingManagerRef.current.setDrawingMode(null); }

  const isDark = theme === 'dark';
  const panelCls = isDark ? 'bg-[#2F3349] border border-[#434968] text-[#CFD3EC]' : 'bg-white border border-gray-200 shadow-card text-gray-800';

  if (!isLoaded) return <div className="w-full h-full flex items-center justify-center bg-secondary/30"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // Clean map style - suppress labels and POIs
  const cleanMapStyles = [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  ];

  return (
    <div className="relative w-full h-full" data-testid="map-view" ref={mapContainerRef}>
      <style>{`
        .gm-style-iw { border-radius: 10px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.18) !important; padding: 2px !important; }
        .gm-style-iw-d { overflow: hidden !important; padding: 10px 12px !important; }
        .gm-style-iw button[aria-label="Close"] { display: none !important; }
        .gm-style-iw-tc { display: none !important; }
        .shape-measurement-label { background: rgba(0,0,0,0.7); padding: 2px 6px; border-radius: 4px; white-space: nowrap; }
        .shape-edge-label { background: rgba(0,0,0,0.55); padding: 1px 4px; border-radius: 3px; white-space: nowrap; }
        .gm-style .shape-measurement-label, .gm-style .shape-edge-label { box-shadow: none !important; filter: none !important; -webkit-filter: none !important; text-shadow: none !important; }
        .gm-style div[style*="font-weight"] { box-shadow: none !important; text-shadow: none !important; filter: none !important; }
        .gm-style div > div > span.shape-measurement-label, .gm-style div > div > span.shape-edge-label { -webkit-text-stroke: 0 !important; paint-order: normal !important; }
        .gm-style [style*="text-stroke"], .gm-style [style*="paint-order"] { -webkit-text-stroke: 0 !important; paint-order: normal !important; }
      `}</style>
      <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={center} zoom={zoom} mapTypeId={mapType} onLoad={onMapLoad}
        options={mapOptions}>
      </GoogleMap>

      {/* Compass Rose */}
      <div className="absolute bottom-24 left-3 z-10 w-14 h-14 pointer-events-none" data-testid="compass-rose">
        <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="28" cy="28" r="27" fill="rgba(0,0,0,0.45)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
          {/* North arrow */}
          <polygon points="28,6 24,24 28,20 32,24" fill="#EA5455"/>
          {/* South arrow */}
          <polygon points="28,50 24,32 28,36 32,32" fill="rgba(255,255,255,0.5)"/>
          {/* East arrow */}
          <polygon points="50,28 32,24 36,28 32,32" fill="rgba(255,255,255,0.4)"/>
          {/* West arrow */}
          <polygon points="6,28 24,24 20,28 24,32" fill="rgba(255,255,255,0.4)"/>
          {/* Labels */}
          <text x="28" y="5" textAnchor="middle" fill="#EA5455" fontSize="7" fontWeight="800" fontFamily="Public Sans,sans-serif">N</text>
          <text x="28" y="55" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="7" fontWeight="700" fontFamily="Public Sans,sans-serif">S</text>
          <text x="54" y="30" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="7" fontWeight="700" fontFamily="Public Sans,sans-serif">E</text>
          <text x="2" y="30" textAnchor="start" fill="rgba(255,255,255,0.7)" fontSize="7" fontWeight="700" fontFamily="Public Sans,sans-serif">W</text>
          {/* Center dot */}
          <circle cx="28" cy="28" r="2.5" fill="white" opacity="0.8"/>
        </svg>
      </div>

      {/* Top Bar - Toolbar pinned to the top-left of the map. In Project
          context the back-to-projects button takes the top-left corner, so
          we offset the toolbar to its right. Quick Look has no button to
          dodge — toolbar gets the full top-left. */}
      <div
        className={`absolute top-1 ${apiPathBase && apiPathBase.startsWith('/projects/') ? 'left-[200px]' : 'left-1'} right-3 z-10 flex items-start gap-2 flex-wrap`}
        data-testid="map-top-bar"
      >
        {!readOnly && (
          <MapToolbar
            panelCls={panelCls} drawingMode={drawingMode} customTool={customTool}
            undoStack={undoStack} redoStack={redoStack} onUndo={undo} onRedo={redo}
            onChangeDrawingMode={changeDrawingMode} onActivateCustomTool={activateCustomTool}
            onSnapshot={handleSnapshot} onExportPDF={onExportPDF}
            panelOpen={panelOpen} onTogglePanel={() => setPanelOpen(p => !p)} measurementCount={measurements.length}
            capabilities={capabilities}
            boundariesEnabled={boundariesEnabled}
            boundariesOpacity={boundariesOpacity}
            boundariesColourIndex={boundariesColourIndex}
            onToggleBoundaries={(v) => setBoundariesEnabled(v)}
            onBoundariesOpacityChange={setBoundariesOpacity}
            onBoundariesColourChange={setBoundariesColourIndex}
          />
        )}
      </div>

      {/* Finish custom tool button */}
      {customTool && customPoints.length >= 2 && !['height', 'text_label', 'add_marker', 'angle'].includes(customTool) && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30">
          <button onClick={() => finishCustomTool(customPoints)} className="bg-primary text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-xl animate-pulse hover:animate-none hover:bg-primary/90 transition-colors" data-testid="finish-custom-btn">
            Finish {customTool === 'arrow' ? 'Arrow' : customTool === 'perimeter' ? 'Perimeter' : 'Line'}
          </button>
        </div>
      )}

      {/* Map Type Switcher + Imagery date pill */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 ${panelCls} rounded-md p-1.5 flex gap-1 z-20 items-center`} data-testid="map-style-switcher">
        {/* Premium feature gate: Imagery date selector is only shown to Pro
            and Enterprise tenants. Free/basic users see just the base
            map-type switcher and always get Live Google Maps imagery. */}
        {capabilities?.imageryDate && (
          <>
            <ImageryDatePill
              releases={waybackReleases}
              value={imageryDate}
              loading={waybackLoading}
              disabled={readOnly}
              panelCls={panelCls}
              onChange={setImageryDate}
            />
            <div className="w-px h-5 bg-border mx-0.5" />
          </>
        )}
        {Object.entries(MAP_TYPES).map(([key, t]) => (
          <button key={key} onClick={() => changeMapType(t.id)} className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-all ${mapType === t.id ? 'bg-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-white/10 opacity-70 hover:opacity-100'}`} data-testid={`map-style-${key}`}>{t.label}</button>
        ))}
        <div className="w-px h-5 bg-border mx-0.5" />
        <ViewModePill value={viewMode} disabled={readOnly} onChange={changeViewMode} />
      </div>

      {/* 360° Street View modal — free for everyone, opens via the ViewModePill. */}
      {streetViewOpen && (
        <StreetViewPanel
          center={mapRef.current ? {
            lat: mapRef.current.getCenter()?.lat?.() ?? project?.map_data?.lat,
            lng: mapRef.current.getCenter()?.lng?.() ?? project?.map_data?.lng,
          } : { lat: project?.map_data?.lat, lng: project?.map_data?.lng }}
          address={project?.map_data?.address || addressText}
          onClose={() => setStreetViewOpen(false)}
        />
      )}

      {/* Measurements Panel */}
      {panelOpen && (
        <MeasurementPanel
          panelCls={panelCls} measurements={measurements}
          onToggleVis={toggleVis} onShowAll={showAll} onHideAll={hideAll}
          onStartDelete={setDeleteIdx} onDuplicate={duplicateShape} onToggleLock={toggleLock}
          onUpdateColor={updateColor} onUpdateOpacity={updateOpacity}
          onSaveEdit={handleSaveEdit} onClose={() => setPanelOpen(false)}
          showSiteLogisticsTab={!!capabilities?.siteLogistics}
        />
      )}

      {/* Shape Properties Panel */}
      {selectedShapeId && (() => {
        const m = measurements.find(ms => ms.id === selectedShapeId);
        const entry = shapesRef.current.find(e => e.id === selectedShapeId);
        if (!m || !entry) return null;
        const st = m.shape_type || m.measurement_type || '';
        return (
          <ShapePropertiesPanel
            measurement={m}
            shape={entry.shape}
            shapeType={st}
            onUpdate={(updates) => {
              setMeasurements(prev => prev.map(item => item.id === selectedShapeId ? { ...item, ...updates } : item));
              if (onUpdateMeasurement && m.id && !m.id.startsWith('temp_')) {
                onUpdateMeasurement(m.id, updates);
              }
              recalcAll();
            }}
            onReplaceShape={handleReplaceShape}
            onDelete={() => {
              const idx = measurements.findIndex(ms => ms.id === selectedShapeId);
              if (idx !== -1) {
                // Remove shape from map
                const entry = shapesRef.current.find(e => e.id === selectedShapeId);
                if (entry?.shape) {
                  // Legacy: orphan crane-circle cleanup for older projects.
                  try { entry.shape.__craneCircle?.setMap?.(null); } catch(ex){}
                  try { entry.shape.setMap(null); } catch(ex){}
                }
                shapesRef.current = shapesRef.current.filter(e => e.id !== selectedShapeId);
                // Remove measurement
                const m = measurements[idx];
                setMeasurements(prev => prev.filter(ms => ms.id !== selectedShapeId));
                if (onDeleteMeasurement && m) onDeleteMeasurement(m);
              }
              setSelectedShapeId(null);
            }}
            onClose={() => setSelectedShapeId(null)}
          />
        );
      })()}

      {/* Property Info Panel — appears when user clicks a cadastre parcel at zoom ≥ 17 */}
      {!selectedShapeId && propertyInfo && (
        <PropertyInfoPanel
          info={propertyInfo}
          onClose={() => {
            // Clear the sticky parcel highlight when the panel closes.
            const layer = parcelDataLayerRef.current;
            const selId = selectedParcelIdRef.current;
            if (layer && selId != null) {
              layer.forEach((f) => {
                if (f.getId() === selId) {
                  try { layer.revertStyle(f); } catch { /* layer/feature may already be torn down on unmount */ }
                }
              });
            }
            selectedParcelIdRef.current = null;
            setPropertyInfo(null);
          }}
          onAddMarker={(lat, lng, label) => {
            const map = mapRef.current;
            if (!map || !window.google?.maps) return;
            const num = shapesRef.current.length + 1;
            // Always create the default pin (icon) — passing `label` here would
            // replace the pin with a text-only DOM node that's invisible on the
            // map. We keep the address on the measurement object (m.label) for
            // display in the measurement list / popups instead.
            const shape = createMarker({
              position: { lat, lng },
              draggable: !readOnly,
              map,
            });
            const m = buildMeasurement(shape, 'marker', num, selectedColor);
            if (label) m.label = label;
            pushUndo();
            const tempId = 'temp_' + Date.now().toString();
            shapesRef.current.push({ shape, id: tempId });
            setMeasurements((prev) => [...prev, { ...m, id: tempId }]);
            attachListeners(shape, 'marker');
            if (onSaveMeasurement) {
              onSaveMeasurement(m).then((saved) => {
                const realId = saved?.id || tempId;
                if (realId !== tempId) {
                  const entry = shapesRef.current.find((s) => s.id === tempId);
                  if (entry) entry.id = realId;
                  setMeasurements((prev) => prev.map((item) => item.id === tempId ? { ...item, id: realId } : item));
                }
              });
            }
          }}
        />
      )}

      {/* Instructions */}
      {!readOnly && measurements.length === 0 && !customTool && !drawingMode && (
        <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 ${panelCls} rounded-md px-4 py-2.5 z-10 text-center max-w-lg`} data-testid="map-instructions">
          <p className="text-xs font-semibold">Draw shapes to measure. All shapes are draggable & editable.</p>
          <p className="text-[10px] opacity-50 mt-1">Polygon/Rect/Circle = Area | Line/Arrow = Distance | Angle = Degrees | Height = Estimate | Marker = Point</p>
        </div>
      )}

      {/* Drawing mode instructions */}
      {drawingMode && !customTool && (
        <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 ${panelCls} rounded-md px-4 py-2.5 z-10 text-center`}>
          <p className="text-xs font-semibold">
            {drawingMode === 'polyline' && 'Click points to draw line. Double-click to finish. Hold Shift to snap to vertex/edge, Ctrl for right angle.'}
            {drawingMode === 'polygon' && 'Click points to draw polygon. Double-click to finish. Hold Shift to snap to vertex/edge, Ctrl for right angle.'}
            {drawingMode === 'rectangle' && 'Click and drag to draw rectangle.'}
            {drawingMode === 'circle' && 'Click and drag to draw circle.'}
          </p>
        </div>
      )}

      {/* Custom tool instructions */}
      {customTool && (
        <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 ${panelCls} rounded-md px-4 py-2.5 z-10 text-center`}>
          <p className="text-xs font-semibold">
            {customTool === 'arrow' && `Click points for arrow direction. Click "Finish" when done.`}
            {customTool === 'angle' && `Click 3 points: first arm, vertex, second arm. Angle auto-calculates.`}
            {customTool === 'height' && `Click building base, then shadow tip. Height estimated from shadow.`}
            {customTool === 'perimeter' && `Click points around the perimeter. Click "Finish" to close & measure.`}
            {customTool === 'multi_distance' && `Click multiple points. Each segment measured. Click "Finish" for total.`}
            {customTool === 'text_label' && `Click on the map to place a text label.`}
            {customTool === 'add_marker' && `Click on the map to place a marker.`}
            {customTool && customTool.startsWith('site_') && (
              `Click on the map to place a ${SITE_LOGISTICS[customTool]?.label || 'site item'}.`
            )}
          </p>
        </div>
      )}

      <ConfirmDeleteDialog open={deleteIdx !== null} onOpenChange={(o) => { if (!o) setDeleteIdx(null); }}
        title="Delete Measurement" description={`Delete "${measurements[deleteIdx]?.label}"? This cannot be undone.`}
        onConfirm={confirmDelete} destructiveLabel="Delete" />
    </div>
  );
}
